import React from "react";
import { Bar, CartesianGrid, Cell, ComposedChart, Line, Pie, PieChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { ChartBar, Target, ChatText } from "@phosphor-icons/react";
import { OUTCOME_META } from "@/components/admin/OutcomeChip";
import { CcPanel, Empty } from "./CcPanel";
import { C, PERIODS, fmtDay, hourLabel, num, pct, tooltipStyle } from "./ccUtils";

const tick = { fill: "#737373", fontSize: 10 };

function Legend({ items }) {
  return (
    <div className="flex items-center gap-4 text-[10px] text-neutral-400 mb-1">
      {items.map(([l, c, dashed]) => (
        <span key={l} className="inline-flex items-center gap-1.5">
          <span className={dashed ? "w-3 border-t-2 border-dashed" : "w-2.5 h-2.5 rounded-sm"} style={dashed ? { borderColor: c } : { background: c }} />{l}
        </span>
      ))}
    </div>
  );
}

export function UsagePanel({ tech, interactive }) {
  const data = tech.trend.map((d) => ({ ...d, label: fmtDay(d.date) }));
  const p = tech.peak;
  return (
    <CcPanel title="Nick usage · last 30 days" icon={ChartBar} testid="panel-tech-assistant-usage" className="h-[292px]" bodyClass="flex flex-col" delay={0.05}
      aside={p && <span data-testid="usage-peak">Busiest: {p.day.slice(0, 3)} {hourLabel(p.hour)}</span>}>
      <Legend items={[["Typed questions", C.green], ["Voice questions", C.sky], ["New chats", C.gold, true]]} />
      <div className="flex-1 min-h-0">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={data} margin={{ top: 8, right: 4, left: -24, bottom: 0 }}>
            <CartesianGrid stroke="#1c1c1c" vertical={false} />
            <XAxis dataKey="label" tick={tick} tickLine={false} axisLine={{ stroke: "#262626" }} interval={4} />
            <YAxis allowDecimals={false} tick={tick} tickLine={false} axisLine={false} />
            {interactive && <Tooltip {...tooltipStyle} />}
            <Bar dataKey="text" name="Typed" stackId="q" fill={C.green} isAnimationActive={false} />
            <Bar dataKey="voice" name="Voice" stackId="q" fill={C.sky} radius={[3, 3, 0, 0]} isAnimationActive={false} />
            <Line dataKey="chats" name="New chats" type="monotone" stroke={C.gold} strokeDasharray="4 3" strokeWidth={2} dot={false} isAnimationActive={false} />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </CcPanel>
  );
}

const ORDER = ["solved", "resolved_by_team", "escalated", "outstanding", "pending"];

export function ResolutionPanel({ tech }) {
  const p30 = tech.by_period["30d"];
  const slices = ORDER.map((k) => ({ key: k, value: p30.outcomes[k] || 0, color: OUTCOME_META[k].color })).filter((s) => s.value);
  const pie = slices.length ? slices : [{ key: "none", value: 1, color: "#1a1a1a" }];
  return (
    <CcPanel title="Resolution · last 30 days" icon={Target} testid="panel-resolution-breakdown" className="h-[236px]" delay={0.1}
      aside={`${num(p30.judged)} chats judged`}>
      <div className="flex items-center gap-5">
        <div className="relative w-[118px] h-[118px] shrink-0">
          <PieChart width={118} height={118}>
            <Pie data={pie} dataKey="value" innerRadius={40} outerRadius={57} paddingAngle={slices.length > 1 ? 2 : 0} stroke="none" isAnimationActive={false}>
              {pie.map((s) => <Cell key={s.key} fill={s.color} />)}
            </Pie>
          </PieChart>
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <span className="font-heading text-xl font-bold text-white" data-testid="resolution-rate-value">{pct(p30.resolution_rate)}</span>
            <span className="text-[9px] text-neutral-500 uppercase tracking-wider">resolved</span>
          </div>
        </div>
        <div className="flex-1 space-y-1">
          {ORDER.map((k) => (
            <div key={k} className="flex items-center gap-2 text-xs" data-testid={`resolution-row-${k}`}>
              <span className="w-2 h-2 rounded-full shrink-0" style={{ background: OUTCOME_META[k].color }} />
              <span className="text-neutral-400 truncate">{OUTCOME_META[k].label}</span>
              <span className="ml-auto font-mono text-white tabular-nums">{num(p30.outcomes[k] || 0)}</span>
            </div>
          ))}
        </div>
      </div>
      <div className="mt-2.5 pt-2 border-t border-neutral-800 grid grid-cols-4 gap-2" data-testid="resolution-periods">
        {PERIODS.map((p) => (
          <div key={p.key} data-testid={`resolution-period-${p.key}`}>
            <div className="text-[10px] font-mono uppercase tracking-wider text-neutral-500">{p.label}</div>
            <div className="font-heading text-lg font-bold tabular-nums" style={{ color: tech.by_period[p.key].resolution_rate == null ? C.muted : C.green }}>{pct(tech.by_period[p.key].resolution_rate)}</div>
          </div>
        ))}
      </div>
    </CcPanel>
  );
}

function QueueStrip({ q }) {
  const items = [["Config reports open", q.discrepancies_open, C.red, "queue-discrepancies"],
    ["Low-confidence chats", q.low_confidence, C.amber, "queue-low-confidence"], ["Flagged chats", q.flagged, C.red, "queue-flagged"]];
  return (
    <div className="grid grid-cols-3 gap-2 border-t border-neutral-800 pt-2 mt-2 shrink-0">
      {items.map(([l, v, c, id]) => (
        <div key={id} data-testid={id}>
          <div className="font-heading text-lg font-bold tabular-nums leading-tight" style={{ color: v ? c : C.muted }}>{num(v)}</div>
          <div className="text-[10px] text-neutral-500 leading-tight">{l}</div>
        </div>
      ))}
    </div>
  );
}

export function TopicsPanel({ tech }) {
  const topics = tech.topics.slice(0, 5);
  const max = Math.max(1, ...topics.map((t) => t.n));
  return (
    <CcPanel title="Top enquiry topics · 30 days" icon={ChatText} testid="panel-enquiry-topics" className="flex-1" bodyClass="flex flex-col" delay={0.15}>
      <div className="flex-1 min-h-0 space-y-2">
        {topics.length ? topics.map((t, i) => (
          <div key={t.topic} data-testid={`topic-row-${i}`}>
            <div className="flex items-center text-xs gap-2">
              <span className="text-neutral-200 truncate">{t.topic}</span>
              <span className="ml-auto font-mono text-neutral-400 tabular-nums">{t.n} · {Math.round(t.pct)}%</span>
            </div>
            <div className="h-1.5 rounded-full bg-neutral-800/70 mt-1">
              <div className="h-full rounded-full" style={{ width: `${(t.n / max) * 100}%`, background: i === 0 ? C.green : "#4f7d31" }} />
            </div>
          </div>
        )) : <Empty>No enquiries in the last 30 days</Empty>}
      </div>
      <QueueStrip q={tech.queue} />
    </CcPanel>
  );
}
